import React, { useState } from 'react';
import { Button } from '../button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../dialog';
import { Delete } from 'lucide-react';

interface DecimalKeypadProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (value: string) => void;
  title: string;
  initialValue?: string;
}

export const DecimalKeypad: React.FC<DecimalKeypadProps> = ({
  isOpen,
  onClose,
  onSubmit,
  title,
  initialValue = '0'
}) => {
  const [displayValue, setDisplayValue] = useState<string>('0');
  const [isFirstInput, setIsFirstInput] = useState<boolean>(true);

  const handleNumberClick = (number: string) => {
    if (isFirstInput) {
      // First input replaces the initial value
      setDisplayValue(number);
      setIsFirstInput(false);
    } else if (displayValue === '0') {
      setDisplayValue(number);
    } else {
      // Check decimal places limit
      const parts = displayValue.split('.');
      if (parts.length === 2 && parts[1].length >= 3) {
        // Max 3 decimal places
        return;
      }
      setDisplayValue(displayValue + number);
    }
  };

  const handleDecimalClick = () => {
    if (isFirstInput) {
      setDisplayValue('0.');
      setIsFirstInput(false);
    } else if (!displayValue.includes('.')) {
      setDisplayValue(displayValue + '.');
    }
  };

  const handleBackspace = () => {
    if (isFirstInput) {
      setDisplayValue('0');
      setIsFirstInput(false);
    } else if (displayValue.length > 1) {
      setDisplayValue(displayValue.slice(0, -1));
    } else {
      setDisplayValue('0');
    }
  };

  const handleClear = () => {
    setDisplayValue('0');
    setIsFirstInput(false);
  };

  const handleSubmit = () => {
    const numValue = parseFloat(displayValue) || 0;
    onSubmit(numValue.toString());
    onClose();
  };

  const handleClose = () => {
    onClose();
  };

  // Reset display value when modal opens
  React.useEffect(() => {
    if (isOpen) {
      const numValue = parseFloat(initialValue) || 0;
      setDisplayValue(numValue.toString());
      setIsFirstInput(true);
    }
  }, [isOpen, initialValue]);

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-lg text-center">{title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Display */}
          <div className="bg-gray-50 border-2 border-gray-200 rounded-lg p-4">
            <div className="text-right text-3xl font-bold text-gray-800 min-h-[3rem] flex items-center justify-end">
              {displayValue}
            </div>
          </div>

          {/* Keypad */}
          <div className="grid grid-cols-3 gap-2">
            {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((num) => (
              <Button
                key={num}
                variant="outline"
                className="h-14 text-xl font-semibold bg-white hover:bg-gray-50 border-gray-300 active:scale-95 transition-transform"
                onClick={() => handleNumberClick(num)}
              >
                {num}
              </Button>
            ))}

            {/* Bottom row: C, 0, Backspace */}
            <Button
              variant="outline"
              className="h-14 text-xl font-semibold bg-red-50 hover:bg-red-100 border-red-200 text-red-700 active:scale-95 transition-transform"
              onClick={handleClear}
            >
              C
            </Button>
            <Button
              variant="outline"
              className="h-14 text-xl font-semibold bg-white hover:bg-gray-50 border-gray-300 active:scale-95 transition-transform"
              onClick={() => handleNumberClick('0')}
            >
              0
            </Button>
            <Button
              variant="outline"
              className="h-14 text-xl font-semibold bg-orange-50 hover:bg-orange-100 border-orange-200 text-orange-700 active:scale-95 transition-transform"
              onClick={handleBackspace}
            >
              <Delete className="h-6 w-6" />
            </Button>

            {/* Decimal point */}
            <Button
              variant="outline"
              className="h-14 text-xl font-semibold bg-blue-50 hover:bg-blue-100 border-blue-200 text-blue-700 active:scale-95 transition-transform"
              onClick={handleDecimalClick}
            >
              .
            </Button>
          </div>

          {/* Action Buttons */}
          <div className="grid grid-cols-2 gap-3 pt-2">
            <Button
              variant="outline"
              onClick={handleClose}
              className="h-12 text-lg"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              className="h-12 text-lg bg-green-600 hover:bg-green-700 text-white"
            >
              Confirmar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
